import React, { createContext, useState, useCallback, useRef } from 'react';
import apiService from '../services/apiService';

export const ScenariosContext = createContext({
  scenarios: [],
  loading: false,
  error: null,
  fetchScenarios: () => {},
  refetchScenarios: () => {}
});

/**
 * Scenarios Provider - Manages scenarios state globally
 * Prevents duplicate API calls by maintaining a single source of truth
 */
export const ScenariosProvider = ({ children }) => {
  const [scenariosByProject, setScenariosByProject] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchInProgressRef = useRef({});

  const fetchScenarios = useCallback(async (projectId, token, forceRefresh = false) => {
    if (!projectId || !token) {
      return [];
    }

    // Return cached scenarios if available and not forcing refresh
    if (!forceRefresh && scenariosByProject[projectId]) {
      return scenariosByProject[projectId];
    }

    // Skip if fetch already in progress for this project
    if (fetchInProgressRef.current[projectId]) {
      return scenariosByProject[projectId] || [];
    }

    fetchInProgressRef.current[projectId] = true;
    setLoading(true);
    setError(null);

    try {
      // Page through the full list — the endpoint defaults to limit=50, which
      // silently truncates projects with hundreds of scenarios.
      const PAGE_SIZE = 200;
      let scenariosArray = [];
      let skip = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const data = await apiService.get(
          `/v1/project/${projectId}/scenarios?limit=${PAGE_SIZE}&skip=${skip}`,
          token
        );
        const page = Array.isArray(data) ? data : (data.scenarios || []);
        scenariosArray = scenariosArray.concat(page);
        const total = Array.isArray(data) ? page.length : (data.total ?? page.length);
        skip += page.length;
        if (page.length === 0 || skip >= total) break;
      }

      // Transform scenarios to consistent format
      const transformedScenarios = scenariosArray.map(scenario => ({
        id: scenario._id || scenario.id,
        _id: scenario._id,
        name: scenario.name,
        description: scenario.description,
        status: scenario.status,
        type: scenario.type || 'Simulation',
        folderId: scenario.folderId || null,
        createdVia: scenario.createdVia,
        locked: scenario.locked || false,
        lockedBy: scenario.lockedBy,
        lockedByName: scenario.lockedByName,
        lockedAt: scenario.lockedAt,
        lastRun: scenario.lastRun,
        createdDate: scenario.createdAt,
        runTime: scenario.runTime,
        recoveryRate: scenario.results?.recovery,
        results: scenario.results
      }));

      // Cache scenarios for this project
      setScenariosByProject(prev => ({
        ...prev,
        [projectId]: transformedScenarios
      }));

      setLoading(false);
      fetchInProgressRef.current[projectId] = false;
      return transformedScenarios;
    } catch (err) {
      console.error('Error fetching scenarios:', err);
      setError(err);
      setLoading(false);
      fetchInProgressRef.current[projectId] = false;
      return [];
    }
  }, [scenariosByProject]);

  const refetchScenarios = useCallback(async (projectId, token) => {
    return fetchScenarios(projectId, token, true);
  }, [fetchScenarios]);

  const value = {
    scenariosByProject,
    loading,
    error,
    fetchScenarios,
    refetchScenarios
  };

  return (
    <ScenariosContext.Provider value={value}>
      {children}
    </ScenariosContext.Provider>
  );
};
